import { useCallback, useRef } from "react";

import useMap from "../objects/useMap";
import { RichTextDocument, RichTextEditorRef, RichTextValue } from "./types";

export type RefsMap<TKey extends string> = Record<TKey, RichTextEditorRef | null>;

export interface RichTextGetters<TKey extends string> {
  getShouldMount: (id: TKey) => boolean;
  getDefaultValue: (id: TKey) => RichTextValue;
  getHandleChange: (id: TKey) => () => void;
  getMountEditor: (id: TKey) => (editor: RichTextEditorRef | null) => void;
}

export type GetRichTextValues = Record<string, RichTextDocument | null>;

export interface RichTextMultipleOptions<TKey extends string> {
  initial: Record<TKey, string>;
  triggerChange: () => void;
}

export const useMultipleRichText = <TKey extends string>({
  initial,
  triggerChange,
}: RichTextMultipleOptions<TKey>) => {
  const editorRefs = useRef<RefsMap<TKey>>({} as RefsMap<TKey>);
  const [shouldMountMap, { set: setShouldMountById }] = useMap();
  const getMountEditor = useCallback(
    (id: TKey) => (ref: RichTextEditorRef | null) => {
      editorRefs.current = {
        ...editorRefs.current,
        [id]: ref,
      };
    },
    [],
  );
  const getHandleChange = (_: TKey) => () => triggerChange();
  const getDefaultValue = useCallback(
    (id: TKey) => {
      if (initial[id] === undefined) {
        setShouldMountById(id, true);

        return null;
      }

      try {
        const result = JSON.parse(initial[id]) as RichTextDocument;

        setShouldMountById(id, true);

        return result;
      } catch (e) {
        setShouldMountById(id, true);

        return initial[id];
      }
    },
    [initial, setShouldMountById],
  );
  const getShouldMount = useCallback(
    (id: TKey) => shouldMountMap.get(id) ?? false,
    [shouldMountMap],
  );
  const getValues = async () => {
    const availableRefs = Object.entries(editorRefs.current).filter(
      ([, value]) => value !== null,
    ) as Array<[string, RichTextEditorRef]>;
    const results = await Promise.all(
      availableRefs.map(async ([key, ref]) => {
        const value = await ref.save();

        return [key, value] as [string, RichTextDocument | null];
      }),
    );

    return Object.fromEntries(results) as GetRichTextValues;
  };

  return {
    getters: {
      getShouldMount,
      getDefaultValue,
      getHandleChange,
      getMountEditor,
    } as RichTextGetters<TKey>,
    getValues,
  };
};
